/**
 * Shown in place of the Troubleshoot content whenever Bluetooth is unavailable.
 * We never enable Bluetooth programmatically (iOS can't) — we tell the cashier
 * exactly where to turn it on. The Control Center screenshot has an inline SVG
 * fallback so the notice is never blank if the asset is missing.
 */
import { Capacitor } from '@capacitor/core';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import MuiRefreshIcon from '@mui/icons-material/Refresh';
import MuiSettingsIcon from '@mui/icons-material/Settings';
import MuiBluetoothIcon from '@mui/icons-material/Bluetooth';
import MuiBluetoothDisabledIcon from '@mui/icons-material/BluetoothDisabled';

import { openAppSettings, promptEnableBluetooth } from 'src/hooks/use-device-permissions';

import MuiIcon from 'src/components/mui-icon';

import BluetoothControlCenterImage from './bluetooth-control-center-image';

// ----------------------------------------------------------------------

interface BluetoothOffNoticeProps {
  /** `radioOff` → Control Center toggle; `permissionOff` → Settings. */
  reason: 'radioOff' | 'permissionOff';
  onRecheck: () => void;
}

export default function BluetoothOffNotice({ reason, onRecheck }: BluetoothOffNoticeProps) {
  const isAndroid = Capacitor.getPlatform() === 'android';
  const permissionOff = reason === 'permissionOff';

  const handleEnableAndroid = async () => {
    await promptEnableBluetooth();
    onRecheck();
  };

  // The Control Center screenshot only applies to the iOS radio-off case.
  const showControlCenterImage = !permissionOff && !isAndroid;

  let instruction: string;
  if (permissionOff) {
    instruction =
      'Bluetooth is turned off for PointCraft. Enable it in Settings to use your Bluetooth printer.';
  } else if (isAndroid) {
    instruction = 'Turn on Bluetooth to connect to your printer.';
  } else {
    instruction = 'Open Control Center and tap the Bluetooth icon to turn it on.';
  }

  return (
    <Stack spacing={2} alignItems="center" sx={{ textAlign: 'center', py: 1 }}>
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'warning.lighter',
        }}
      >
        <MuiIcon icon={MuiBluetoothDisabledIcon} width={34} sx={{ color: 'warning.dark' }} />
      </Box>

      <Box>
        <Typography variant="h6">Bluetooth is off</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {instruction}
        </Typography>
      </Box>

      {showControlCenterImage && <BluetoothControlCenterImage />}

      <Stack direction="row" spacing={1.5} sx={{ pt: 0.5 }}>
        {permissionOff && (
          <Button
            variant="contained"
            startIcon={<MuiIcon icon={MuiSettingsIcon} />}
            onClick={() => {
              openAppSettings();
            }}
          >
            Open Settings
          </Button>
        )}
        {!permissionOff && isAndroid && (
          <Button
            variant="contained"
            startIcon={<MuiIcon icon={MuiBluetoothIcon} />}
            onClick={() => {
              handleEnableAndroid();
            }}
          >
            Enable Bluetooth
          </Button>
        )}
        <Button variant="outlined" startIcon={<MuiIcon icon={MuiRefreshIcon} />} onClick={onRecheck}>
          Re-check
        </Button>
      </Stack>
    </Stack>
  );
}
